import { getSiteLocation } from 'aos-config-loader'
import {
    createEnumFromKeys,
    createEnumFromNames,
    createEnumFromRecord,
} from 'aos-helpers/src/helpers/Enum'
import { translateEnum } from 'aos-helpers/src/helpers/translations/Translations'
import { languageProvider } from 'aos-singleton'
import { BaseTheme } from 'aos-ui/src/shared/types/themeTypes'
import { IconName } from 'aos-ui-universal/src/icons/compiled'
import { EnumValues } from 'enum-values'
import { TFunction } from 'i18next'
import { capitalize, chain, entries, flatMap, keys, pickBy, reduce, uniq, values } from 'lodash'

import { TaskVariant } from '../../../dataaccess/tasks/types/TaskDto'
import { BaggageDeliveryConfig } from '../../airportStatus/baggage/types/BaggageDeliveryConfig'
import { Role } from '../../auth/types/Role'
import { MapSiteLocationCustomization } from '../../common/types/MapSiteLocationCustomization'
import { FlightCode } from '../../iata/types/FlightCode'
import { DailyOpsConfig } from '../../operationalForecast/DailyOpsConfig'
import { DashboardItemType } from '../../statusDashboard/types/DashboardItemType'
import { TaskProcessType } from '../../tasks/types/TaskProcessType'
import { FlightListStalenessConfig } from './FlightListStalenessConfig'

const siteLocation = getSiteLocation()
// SiteLocationConfig uses generic string types; narrow to domain-specific AirportConfig
const airportsJson = siteLocation as unknown as AirportConfig

export type NotificationPreferenceType = {
    [key: string]: { categories: string[]; groupName: string }
}

type AirportConfig = {
    airports: {
        [key: string]: AirportDetails
    }
    processes: {
        [key: string]: ProcessConfig
    }
    notificationPreferenceTypes: NotificationPreferenceType
    handlingAgents: string[]
    operationalForecastGroups: Array<Omit<ProcessCategory, 'hideOnEmergency'>>
    priorityAirlines: FlightCode[]
    supportEmail: string
    format: string
    privacyPolicyUrl: { [key: string]: string }
    termsAndConditionsUrl?: { [key: string]: string }
    taskManagerTabs: string[]
    taskReportingItems: string[]
    mapVariants: Record<string, string>
    mainAirportMapVariants: MapVariant[]
    taskReportingBottomContacts: {
        mainAirportContacts: Array<{ title: string; phoneNumber: string }>
        networkContacts: Array<{ title: string; phoneNumber: string }>
    }
    taskReportingAdditionalUrls: TaskReportingAdditionalUrl[]
    restrictionsConfig: RestrictionConfig
    onboardingMainTextTheme: BaseTheme
    flightInformationTabs: string[]
    onboardingAnimationSteps: number
    onboardingAnimationLoadEnd: number
    roleManager: {
        checklistManager: Record<string, Role[]>
    }
    exportTaskDataItems: { taskVariant: TaskVariant; roles: Role[] }[]
    statusDashboardConfig: StatusDashboardConfig
    flightTableConfig: {
        minDelayTime: number
        delayedFlightThresholdMinutes?: number
    }
    centralPosition: {
        lat: number
        lon: number
        zoom: number
    }
    paxForecastChartEdges: [number, number]
    notamSource: string
    dailyOpsConfig: DailyOpsConfig
    baggageDeliveryConfig: BaggageDeliveryConfig
    flightListStaleness?: FlightListStalenessConfig
    networkAirport: { name: string; type: string }
    taskProcessType: TaskProcessType[]
    moduleVisibility: {
        global: {
            allowDownloadFlightList: boolean
            icaoIataSwitch: boolean
            atcRealtimeAlerting: boolean
            weatherNoticesEnabled: boolean
            widgetNotificationsEnabled: boolean
            flightGoToDayEnabled: boolean
            multiGroupEventsEnabled: boolean
            eventSchedulingAllSeveritiesEnabled: boolean
            nonApocEventsListEnabled: boolean
            airportContextSwitchDisabled?: boolean
            fodListFollowsTaskAccess?: boolean
        }
        perAirport: {
            discountModuleAirport?: string | null
            widgetManagementAirport?: string | null
        }
    }
    arcgisOrganisationId: string
    arcgisServicesHost?: string
}

export type ProcessCategories = {
    categories: ProcessCategory[]
}
export type ProcessConfig = ProcessCategories & {
    isAtc: boolean
    isNetwork: boolean
    locationRequired: boolean
    order: number
}

export type ProcessCategory = {
    icon: IconName
    name: string
    hideOnEmergency?: boolean
}

export const {
    processes: processCategories,
    handlingAgents,
    operationalForecastGroups,
    format,
    priorityAirlines = [],
    supportEmail,
    privacyPolicyUrl: policyUrl = {},
    termsAndConditionsUrl: termsUrl = {},
    taskManagerTabs,
    mapVariants = {},
    mainAirportMapVariants = [],
    taskReportingItems,
    taskReportingBottomContacts,
    taskReportingAdditionalUrls = [],
    restrictionsConfig = {
        withAreaTypeNumbers: false,
        withGroupedStats: false,
        areaTypes: [],
        resourceTypes: [],
    },
    onboardingMainTextTheme = 'light',
    flightInformationTabs = [],
    onboardingAnimationSteps = 1,
    onboardingAnimationLoadEnd = 1,
    roleManager,
    exportTaskDataItems,
    statusDashboardConfig,
    flightTableConfig,
    paxForecastChartEdges,
    notamSource,
    dailyOpsConfig,
    baggageDeliveryConfig,
    flightListStaleness,
    networkAirport,
    taskProcessType,
} = airportsJson

export const DEFAULT_DELAYED_FLIGHT_THRESHOLD_MINUTES = 16
export const delayedFlightThresholdMinutes =
    flightTableConfig?.delayedFlightThresholdMinutes ?? DEFAULT_DELAYED_FLIGHT_THRESHOLD_MINUTES

export const isNotamSourceConfigured = (source: string | undefined): boolean =>
    !!source && source.trim().length > 0

export const notamSourceConfigured = isNotamSourceConfigured(notamSource)

// Defensive defaults: missing moduleVisibility blocks (e.g. legacy tenant configs)
// degrade to "all features off" instead of crashing at module-load time.
const DEFAULT_MODULE_VISIBILITY: AirportConfig['moduleVisibility'] = {
    global: {
        allowDownloadFlightList: false,
        icaoIataSwitch: false,
        atcRealtimeAlerting: false,
        weatherNoticesEnabled: false,
        widgetNotificationsEnabled: false,
        flightGoToDayEnabled: false,
        multiGroupEventsEnabled: false,
        eventSchedulingAllSeveritiesEnabled: false,
        nonApocEventsListEnabled: false,
        airportContextSwitchDisabled: false,
        fodListFollowsTaskAccess: false,
    },
    perAirport: {},
}

const {
    global: visGlobal = DEFAULT_MODULE_VISIBILITY.global,
    perAirport: visPerAirport = DEFAULT_MODULE_VISIBILITY.perAirport,
} = airportsJson.moduleVisibility ?? DEFAULT_MODULE_VISIBILITY
export const {
    allowDownloadFlightList = false,
    icaoIataSwitch = false,
    atcRealtimeAlerting = false,
    weatherNoticesEnabled = false,
    widgetNotificationsEnabled = false,
    flightGoToDayEnabled = false,
    multiGroupEventsEnabled = false,
    eventSchedulingAllSeveritiesEnabled = false,
    nonApocEventsListEnabled = false,
    airportContextSwitchDisabled = false,
} = visGlobal
export const { discountModuleAirport, widgetManagementAirport } = visPerAirport

/**
 * AOS-1276 — every legacy role that opens at least one checklist-manager tab
 * (`roleManager.checklistManager` in the tenant config, see `ChecklistManagerList`).
 */
export const checklistManagerRoles: Role[] = uniq(
    flatMap(values(roleManager?.checklistManager ?? {})),
)

export const atcProcessCategories = pickBy(processCategories, q => q.isAtc)
export const processList = chain(processCategories)
    .toPairs()
    .sortBy(([_, value]) => value.order)
    .map(([key]) => key)
    .value()
export const atcProcessList = chain(processCategories)
    .pickBy(p => p.isAtc)
    .toPairs()
    .sortBy(([_, value]) => value.order)
    .map(([key]) => key)
    .value()

export const getAirportByCode = (code: string) => typedAirports.airports[code]

const iconCategoryMap = flatMap(processCategories, q => q.categories).reduce(
    (acc, q) => {
        acc[q.name] = q.icon
        return acc
    },
    {} as Record<string, IconName>,
)

export const processCategoryIcon = (category: string) => iconCategoryMap[category] as IconName

export const typedAirports = airportsJson

export const AosAirport = reduce(
    typedAirports.airports,
    (acc, _, key) => {
        acc[key] = key
        return acc
    },
    { [networkAirport?.name]: networkAirport?.name } as Record<string, string>,
)
export type AosAirport = keyof typeof AosAirport

export const MainAirport = entries(typedAirports.airports).find(
    q => q[1].mainAirport,
)?.[0] as AosAirport

/**
 * Three-state per-airport feature gate predicate.
 *   configuredAirport === undefined → feature disabled (returns false)
 *   configuredAirport === null      → feature unrestricted (returns true)
 *   otherwise                       → visible only when userLocation matches
 */
export const canSeeForAirport = (
    configuredAirport: string | null | undefined,
    userLocation: AosAirport | null | undefined,
): boolean => {
    if (configuredAirport === undefined) {
        return false
    }
    if (configuredAirport === null) {
        return true
    }
    return userLocation === configuredAirport
}

/**
 * Registry of per-airport module gates. Each key maps to a value sourced from
 * `siteLocation.moduleVisibility.perAirport.*`. To add a new gateable module:
 *   1. Add the key to `ModuleVisibilityConfig.perAirport` in aos-config-loader/types.ts
 *   2. Add it to this registry below
 *   3. Use `canSeeModule('newKey', location)` at the route / menu / component boundary
 *   4. Document the default (omit from config = hidden) for every tenant
 */
const perAirportModuleGates = {
    discountModule: discountModuleAirport,
    widgetManagement: widgetManagementAirport,
} as const

export type ConfigurableModuleKey = keyof typeof perAirportModuleGates

export const canSeeModule = (
    moduleKey: ConfigurableModuleKey,
    userLocation: AosAirport | null | undefined,
): boolean => canSeeForAirport(perAirportModuleGates[moduleKey], userLocation)

export const canSeeDiscountModule = (location: AosAirport | null | undefined) =>
    canSeeModule('discountModule', location)

export const canSeeWidgetManagement = (location: AosAirport | null | undefined) =>
    canSeeModule('widgetManagement', location)
export interface AirportDetails {
    iata: string
    icao: string
    name: string
    city: string
    country: string
    latitude: number
    longitude: number
    mainAirport?: boolean // Optional because not all airports might have this field
    atcConfig?: {
        latitude: number
        longitude: number
        zoom: number
        extent: {
            bbox: number[]
            source: string
            destination: string
        }
    }
}

export const allAirports = EnumValues.getValues<AosAirport>(AosAirport).filter(
    airport => airport !== networkAirport?.name,
)

export const locationName = (t: TFunction) => translateEnum<AosAirport>(t)(AosAirport, 'airports')
export const orderedAirportNames = (t: TFunction) =>
    chain(allAirports)
        .orderBy(q => locationName(t)(q))
        .value()

export const airportIcon = (e: AosAirport) => capitalize((e as string).toLowerCase())

export const toSiteLocationCustomization = (
    aosAirport: AosAirport,
): MapSiteLocationCustomization =>
    aosAirport === MainAirport
        ? MapSiteLocationCustomization.MainAirport
        : MapSiteLocationCustomization.Other
export const NotificationsPreferencesEnum = createEnumFromKeys(
    values(airportsJson.notificationPreferenceTypes)
        .map(q => q.groupName)
        .reduce(
            (acc, q) => {
                acc[q] = q
                return acc
            },
            {} as Record<string, string>,
        ),
)
export type NotificationsPreferencesType = keyof typeof NotificationsPreferencesEnum

export const allNotificationsPreferencesTypes = keys(airportsJson.notificationPreferenceTypes)

export const notificationPreferences = airportsJson.notificationPreferenceTypes

export type AosHandlingAgentType = (typeof handlingAgents)[number]

export const AosHandlingAgent = Object.freeze(
    handlingAgents?.reduce(
        (acc, value) => {
            acc[value] = value
            return acc
        },
        {} as Record<AosHandlingAgentType, AosHandlingAgentType>,
    ),
)
export const mapHandlingAgentToCompany = (t: TFunction) => (agent: AosHandlingAgentType) =>
    translateEnum<AosHandlingAgentType>(t)(
        AosHandlingAgent,
        'flight-information.handling-agent',
        AosHandlingAgent.Unknown,
    )(agent)

export const OperationalForecastGroup = reduce(
    operationalForecastGroups,
    (acc, value) => {
        acc[value.name] = value.name
        return acc
    },
    {} as Record<string, string>,
)

export const renderOperationalTile = (aosAirport: AosAirport): boolean => {
    return aosAirport === MainAirport
        ? dailyOpsConfig.visibility.main
        : dailyOpsConfig.visibility.network
}

export type OperationalForecastGroup = keyof typeof OperationalForecastGroup

export const getOperationalForecastGroupIcon = (name: OperationalForecastGroup) =>
    operationalForecastGroups.find(el => el.name === name)!.icon

export const MapVariant = createEnumFromRecord(mapVariants)
export type MapVariant = keyof typeof MapVariant

// Falls back to the fallback locale rather than the current one: a tenant that
// configures only `en-US` would otherwise leave every other locale with no URL
// at all — silently hiding the link, or (before this) throwing on the `sv-SE`
// Swedavia build whose config is keyed `en-US`/`fi-FI`. `undefined` now means
// "this tenant configured no URL", which callers must handle.
const configuredUrl = (urls: { [key: string]: string }): string | undefined =>
    urls[languageProvider.getCurrent()] ?? urls[languageProvider.getFallback()]

export const getPrivacyPolicyUrl = () => configuredUrl(policyUrl)

export const getTermsAndConditionsUrl = () => configuredUrl(termsUrl)

export const RestrictionResourceType: Record<string, string> = createEnumFromNames(
    restrictionsConfig.resourceTypes,
)
export type RestrictionResourceType = keyof typeof RestrictionResourceType

export const RestrictionAreaType: Record<string, string> = createEnumFromNames(
    restrictionsConfig.areaTypes,
)
export type RestrictionAreaType = keyof typeof RestrictionAreaType

export type TaskReportingAdditionalUrl = {
    title: string
    description: string
    url: string
}

export type RestrictionConfig = {
    withAreaTypeNumbers: boolean
    withGroupedStats: boolean
    areaTypes: RestrictionAreaTypeConfig[]
    resourceTypes: RestrictionResourceTypeConfig[]
}

export type RestrictionResourceTypeConfig = {
    name: string
    icon: string
    restrictionIcon: string
    key: string
}

export type RestrictionAreaTypeConfig = {
    name: string
    areaTypeKey: string
    areaNumbers: number[]
}

export enum StatusDashboardFlavor {
    DEFAULT = 'DEFAULT',
    HIDDEN = 'HIDDEN',
}

type StatusDashboardFlavorType = StatusDashboardFlavor | string

export type StatusDashboardConfig = {
    allowAdminDismissWarning: boolean
    showWeatherBarsForNetworkAirports: boolean
    widgets: Record<
        Partial<DashboardItemType>,
        {
            flavor?: StatusDashboardFlavorType
            flavorOverride?: Record<string, StatusDashboardFlavorType>
            hiddenSubsections?: string[]
        }
    >
}

// Stable identity so callers can pass the result straight into connect()/reselect without
// defeating shallow comparison on every render. Shared across all callers — read only, never
// mutate it.
const EMPTY_SUBSECTIONS: string[] = []

export const getHiddenWidgetSubsections = (itemType: DashboardItemType): string[] =>
    statusDashboardConfig?.widgets?.[itemType]?.hiddenSubsections ?? EMPTY_SUBSECTIONS

export const isWidgetSubsectionHidden = (
    itemType: DashboardItemType,
    subsection: string,
): boolean => getHiddenWidgetSubsections(itemType).includes(subsection)
